import { map, times } from 'lodash';
import { Component, ReactChild, ReactNode } from 'react';
import { DefaultTheme, withTheme } from 'styled-components';
import { KeyValue } from '@mews/enums';
import { isAtMostMobile, withBreakpointRange } from '@optimus-web/breakpoints';
import { ANIMATION_DURATION } from '@optimus-web/common';
import { Positioner } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { Button } from '../Button';
import { Divider } from '../Divider';
import { TooltipProps } from '../Tooltip';
import { BreakpointRangeWithFallback } from '../types';
import { getBreakpointRangeValueFallback } from '../utils/breakpointsFallback';
import { CalendarService } from '../utils/services';
import CalendarMonthGrid from './CalendarMonthGrid';
import CalendarMonthSelector from './CalendarMonthSelector';
import { updateContainerWidth } from './MonthlyCalendarUtils/LifeCycleManagement';
import {
    CalendarFooter,
    CalendarMonthGridContainerElement,
    CalendarMonthSelectorWrapperElement,
    CalendarViewHolderElement,
    DayNameElement,
    DayNamesElement,
    DayNamesWeekElement,
    NextMonthButtonWrapper,
    NextMonthIconButton,
    PrevMonthButtonWrapper,
    PrevMonthIconButton,
    SectoredCalendarFooter,
} from './style';
import { Modifiers } from './types';

export interface CalendarOriginalProps<D, M, Y> {
    getCalendarService: () => CalendarService<D, M, Y>;
    allowDisabledClick?: boolean;
    enableAdjacentMonthSelection?: boolean;
    areSurroundingDaysOfSingleMonthHidden?: boolean;
    className?: string;
    dataTestName?: string;
    date?: D;
    endDate?: D | null;
    isSingleDaySelectionCircle?: boolean;
    leftFooter?: ReactNode;
    rightFooter?: ReactNode;
    maxDate?: D;
    minDate?: D;
    modifiers?: Modifiers<D>;
    month?: M;
    nextMonthLabel?: ReactChild;
    numberOfMonths: number;
    onDateClick?: (event: React.MouseEvent, date: D) => void;
    onMonthChange?: (month: M) => void;
    previousMonthLabel?: ReactChild;
    startDate?: D;
    submitButtonHandler?: () => void;
    submitButtonLabel?: ReactNode;
    startDateWithTooltip?: TooltipProps;
    getDateTooltip?: (day: D) => ReactNode | undefined;
    tooltipDelay?: number;
}

type CalendarProps<D, M, Y> = CalendarOriginalProps<D, M, Y> & {
    breakpointRange: BreakpointRangeWithFallback;
    theme: DefaultTheme;
};

type CalendarType = <D, M, Y>(
    props: CalendarOriginalProps<D, M, Y>
) => React.ReactElement<CalendarOriginalProps<D, M, Y>>;

interface CalendarState<D, M> {
    month: M;
    dayNames: string[];
    monthSelectorVisible: boolean;
    monthSelected: M;
    skipAnimateOut: boolean;
    focusedDate: D;
}

class Calendar<D, M, Y> extends Component<CalendarProps<D, M, Y>, CalendarState<D, M>> {
    private monthGridContainerElement: HTMLElement | null = null;
    private currentDate: D;
    private tooltippedDay: Positioner | null = null;

    static defaultProps = {
        modifiers: {},
        numberOfMonths: 1,
    };

    constructor(props: CalendarProps<D, M, Y>) {
        super(props);

        const { getCalendarService } = props;
        const month = props.month || getCalendarService().getMonthOfDate(getCalendarService().getCurrentDate());
        const dayNames = getCalendarService().getDayNames();

        this.currentDate = getCalendarService().getCurrentDate();
        this.state = {
            month,
            dayNames,
            monthSelectorVisible: false,
            monthSelected: month,
            skipAnimateOut: true,
            focusedDate: this.currentDate,
        };
    }

    tooltippedDayRef = (c: Positioner | null) => (this.tooltippedDay = c);

    componentDidMount() {
        updateContainerWidth(
            this.monthGridContainerElement,
            this.props.numberOfMonths,
            this.props.breakpointRange,
            this.props.theme
        );
        const { month } = this.state;
        this.setState({ focusedDate: this.getInitialFocusedDate(month) });
    }

    componentDidUpdate(prevProps: CalendarProps<D, M, Y>) {
        if (prevProps.numberOfMonths !== this.props.numberOfMonths) {
            updateContainerWidth(
                this.monthGridContainerElement,
                this.props.numberOfMonths,
                this.props.breakpointRange,
                this.props.theme
            );
        }

        if (
            prevProps.month &&
            this.props.month &&
            !this.props.getCalendarService().isMonthEqual(prevProps.month, this.props.month)
        ) {
            this.changeMonth(this.props.month);
        }
    }

    handlePrevClick = () => {
        const { month, monthSelectorVisible } = this.state;
        const prevMonth = this.props.getCalendarService().getPrevMonth(month);
        this.changeMonth(prevMonth);

        if (monthSelectorVisible) {
            this.setState({ monthSelectorVisible: false, skipAnimateOut: true });
        }
    };

    handleNextClick = () => {
        const { month, monthSelectorVisible } = this.state;
        const nextMonth = this.props.getCalendarService().getNextMonth(month);

        this.changeMonth(nextMonth);

        if (monthSelectorVisible) {
            this.setState({ monthSelectorVisible: false, skipAnimateOut: true });
        }
    };

    handleMonthSelect = (e: React.MouseEvent | React.KeyboardEvent<HTMLButtonElement>, month: M) => {
        this.setState({ monthSelectorVisible: false });

        const months = this.generateMonths({
            month: this.state.month,
            numberOfMonths: this.props.numberOfMonths,
        });
        const monthSelectedPosition = this.getMonthUniquePosition(this.state.monthSelected, months);
        const newMonth = this.props.getCalendarService().subtractMonths(month, monthSelectedPosition);

        if (!this.props.getCalendarService().isMonthEqual(month, this.state.monthSelected)) {
            this.changeMonth(newMonth);
        }
    };

    handleDateClick = (e: React.MouseEvent, daySelected: D) => {
        if (this.props.enableAdjacentMonthSelection) {
            const calendarService = this.props.getCalendarService();
            const monthOfSelectedDay = calendarService.getMonthOfDate(daySelected);

            if (
                !calendarService.isMonthEqual(monthOfSelectedDay, this.state.month) &&
                calendarService.isDateGreaterOrEqual(daySelected, calendarService.getCurrentDate()) &&
                this.props.numberOfMonths === 1
            ) {
                this.setState({
                    month: monthOfSelectedDay,
                });
            }
        }

        this.props.onDateClick?.(e, daySelected);
    };

    handleMonthNameClick = (monthSelected: M) => {
        const prevMonthSelectedIndex = this.props.getCalendarService().getMonthIndex(this.state.monthSelected);
        const currentMonthSelectedIndex = this.props.getCalendarService().getMonthIndex(monthSelected);
        if (currentMonthSelectedIndex === prevMonthSelectedIndex && this.state.monthSelectorVisible === true) {
            this.setState({ monthSelectorVisible: false });
        } else {
            this.setState({ monthSelectorVisible: true, skipAnimateOut: false });
        }

        this.setState({ monthSelected });
    };

    changeMonth = (month: M) => {
        this.props.onMonthChange?.(month);
        this.setState({ month, focusedDate: this.getInitialFocusedDate(month) });

        window.setTimeout(() => {
            if (this.tooltippedDay) {
                this.tooltippedDay.updatePlacement();
            }
        }, ANIMATION_DURATION.default);
    };

    monthGridContainerRef = (c: HTMLElement | null) => (this.monthGridContainerElement = c);

    generateMonths({ month, numberOfMonths }: { month: M; numberOfMonths: number }) {
        return times(numberOfMonths, (i) => this.props.getCalendarService().addMonths(month, i));
    }

    getMonthSelectedPosition = (monthSelectedIndex: number, months: M[]) => {
        const monthSelectedFind = months.map((item) => {
            const monthEquality = this.props.getCalendarService().getMonthIndex(item) === monthSelectedIndex;
            return monthEquality;
        });
        const selectedPosition = monthSelectedFind.findIndex((i) => i === true);
        return selectedPosition;
    };

    getMonthUniquePosition = (month: M, months: M[]) => {
        const monthSelectedFind = months.map((item) => {
            const monthEquality = this.props.getCalendarService().isMonthEqual(item, month);
            return monthEquality;
        });
        const selectedPosition = monthSelectedFind.findIndex((i) => i === true);
        return selectedPosition;
    };

    getInitialFocusedDate = (month: M) => {
        const { getCalendarService, numberOfMonths, date, startDate } = this.props;
        const { getMonthOfDate, isMonthEqual, getMonthFirstDate } = getCalendarService();
        const months = this.generateMonths({ month, numberOfMonths });

        const isPartOfMonths = (day: D) => {
            const monthEqualityArray = months.map((el) => {
                const monthOfDate = getMonthOfDate(day);
                const monthEquality = isMonthEqual(monthOfDate, el);
                return monthEquality;
            });

            return monthEqualityArray.includes(true);
        };

        if (date && isPartOfMonths(date)) {
            return date;
        }

        if (startDate && isPartOfMonths(startDate)) {
            return startDate;
        }

        if (this.currentDate && isPartOfMonths(this.currentDate)) {
            return this.currentDate;
        }

        return getMonthFirstDate(months[0]);
    };

    setFocusedDate = (focusedDate: D) => {
        const { getCalendarService } = this.props;
        const { getDateString } = getCalendarService();
        const targetDateString = getDateString(focusedDate);

        const targetSelector = document.querySelector(`[data-date='${targetDateString}']`) as HTMLElement;
        targetSelector?.focus();
    };

    isDateOutOfView = (date: D) => {
        const { getCalendarService, numberOfMonths } = this.props;
        const { month } = this.state;
        const { isMonthGreater, addMonths, getMonthOfDate } = getCalendarService();

        const newFocusedMonth = getMonthOfDate(date);
        const firstMonth = month;
        const lastMonth = addMonths(month, numberOfMonths - 1);

        return isMonthGreater(firstMonth, newFocusedMonth) || isMonthGreater(newFocusedMonth, lastMonth);
    };

    handleKeyDown = (e: KeyboardEvent) => {
        const { getCalendarService, numberOfMonths, modifiers } = this.props;
        const { focusedDate, month } = this.state;
        const { getPrevDate, getNextDate, getPrevWeek, getNextWeek, getMonthOfDate, subtractMonths } =
            getCalendarService();

        const prevDate = getPrevDate(focusedDate);
        const nextDate = getNextDate(focusedDate);
        const prevWeek = getPrevWeek(focusedDate);
        const nextWeek = getNextWeek(focusedDate);
        const months = this.generateMonths({ month, numberOfMonths });
        const isDisabled = (date: D) => modifiers && modifiers.disabled && modifiers.disabled(date);

        let handled;

        switch (e.key) {
            case KeyValue.ArrowUp:
                if (!isDisabled(prevWeek)) {
                    if (this.isDateOutOfView(prevWeek)) {
                        const prevWeekNewMonth = subtractMonths(getMonthOfDate(prevWeek), months.length - 1);
                        this.changeMonth(prevWeekNewMonth);
                        this.setState({ focusedDate: prevWeek });
                        window.setTimeout(() => this.setFocusedDate(prevWeek), ANIMATION_DURATION.default);
                    }
                    this.setState({ focusedDate: prevWeek });
                    this.setFocusedDate(prevWeek);
                }
                handled = true;
                break;

            case KeyValue.ArrowDown:
                if (!isDisabled(nextWeek)) {
                    if (this.isDateOutOfView(nextWeek)) {
                        this.changeMonth(getMonthOfDate(nextWeek));
                        this.setState({ focusedDate: nextWeek });
                        window.setTimeout(() => this.setFocusedDate(nextWeek), ANIMATION_DURATION.default);
                    }
                    this.setState({ focusedDate: nextWeek });
                    this.setFocusedDate(nextWeek);
                }
                handled = true;
                break;

            case KeyValue.ArrowLeft:
                if (!isDisabled(prevDate)) {
                    if (this.isDateOutOfView(prevDate)) {
                        const prevDateNewMonths = subtractMonths(getMonthOfDate(prevDate), months.length - 1);
                        this.changeMonth(prevDateNewMonths);
                        this.setState({ focusedDate: prevDate });
                        window.setTimeout(() => this.setFocusedDate(prevDate), ANIMATION_DURATION.default);
                    }
                    this.setState({ focusedDate: prevDate });
                    this.setFocusedDate(prevDate);
                }
                handled = true;
                break;

            case KeyValue.ArrowRight:
                if (!isDisabled(nextDate)) {
                    if (this.isDateOutOfView(nextDate)) {
                        this.changeMonth(getMonthOfDate(nextDate));
                        this.setState({ focusedDate: nextDate });
                        window.setTimeout(() => this.setFocusedDate(nextDate), ANIMATION_DURATION.default);
                    }
                    this.setState({ focusedDate: nextDate });
                    this.setFocusedDate(nextDate);
                }
                handled = true;
                break;

            default:
                break;
        }

        if (handled) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    render() {
        const {
            className,
            dataTestName,
            endDate,
            getCalendarService,
            minDate,
            maxDate,
            numberOfMonths,
            modifiers,
            previousMonthLabel,
            nextMonthLabel,
            onDateClick,
            onMonthChange,
            submitButtonHandler,
            submitButtonLabel,
            leftFooter,
            rightFooter,
            startDate,
            startDateWithTooltip,
            getDateTooltip,
            breakpointRange,
            ...other
        } = this.props;
        const { dayNames, month, monthSelectorVisible, monthSelected, focusedDate } = this.state;

        const allowedRange = { minDate, maxDate };

        const months = this.generateMonths({ month, numberOfMonths });

        const monthSelectedIndex = getCalendarService().getMonthIndex(monthSelected);
        const monthSelectedPosition = this.getMonthSelectedPosition(monthSelectedIndex, months);

        const weeksOfMonth = (index: number) =>
            getCalendarService().getWeeksOfMonth(getCalendarService().addMonths(month, index));
        const numberOfWeeks = Math.max(...times(numberOfMonths, (i) => weeksOfMonth(i).length));

        const monthGridContainerProps = {
            ref: this.monthGridContainerRef,
        };

        const isMobile = isAtMostMobile(getBreakpointRangeValueFallback(this.props.breakpointRange));

        const monthGridProps = {
            ...other,
            ...allowedRange,
            endDate,
            getCalendarService,
            month,
            numberOfMonths,
            onDateClick: this.handleDateClick,
            onMonthNameClick: this.handleMonthNameClick,
            monthSelectorVisible,
            monthSelectedIndex,
            months,
            modifiers: {
                ...modifiers,
                today: (date: D) => getCalendarService().isDateEqual(date, this.currentDate),
            },
            handleKeyDown: this.handleKeyDown,
            focusedDate,
            startDate,
            startDateWithTooltip,
            dayWithTooltipRef: this.tooltippedDayRef,
            getDateTooltip,
            isMobile,
        };

        const viewHolderElementProps = {
            monthSelectorVisible,
            numberOfWeeks,
            numberOfMonths,
            isMobile,
        };

        const monthSelectorWrapperProps = {
            numberOfWeeks,
            monthSelectorVisible,
            monthSelectedPosition,
            skipAnimateOut: this.state.skipAnimateOut,
            isMobile,
        };

        const isSingleMonth = numberOfMonths === 1;

        return (
            <div style={isMobile ? { width: '100%' } : undefined}>
                <CalendarViewHolderElement {...viewHolderElementProps} data-test-calendar={dataTestName}>
                    <CalendarMonthGridContainerElement {...monthGridContainerProps}>
                        <PrevMonthButtonWrapper>
                            <PrevMonthIconButton
                                icon={IconName.ChevronLeft}
                                variant="ghost"
                                onClick={this.handlePrevClick}
                                disabled={getCalendarService().isPastMonthDisabled(month, allowedRange)}
                                accessibleText={previousMonthLabel}
                                data-test-month-previous
                                iconSize="medium"
                                type="button"
                            />
                        </PrevMonthButtonWrapper>
                        <NextMonthButtonWrapper>
                            <NextMonthIconButton
                                icon={IconName.ChevronRight}
                                variant="ghost"
                                onClick={this.handleNextClick}
                                disabled={getCalendarService().isFutureMonthDisabled(month, allowedRange)}
                                accessibleText={nextMonthLabel}
                                data-test-month-next
                                iconSize="medium"
                                type="button"
                            />
                        </NextMonthButtonWrapper>
                        <CalendarMonthGrid {...monthGridProps} />
                        <DayNamesElement aria-hidden isMobile={isMobile}>
                            {times(numberOfMonths, (n) => (
                                <DayNamesWeekElement key={n} isMobile={isMobile}>
                                    {map(dayNames, (name) => (
                                        <DayNameElement key={name}>{name}</DayNameElement>
                                    ))}
                                </DayNamesWeekElement>
                            ))}
                        </DayNamesElement>
                    </CalendarMonthGridContainerElement>
                    <CalendarMonthSelectorWrapperElement {...monthSelectorWrapperProps}>
                        <CalendarMonthSelector
                            numberOfWeeks={numberOfWeeks}
                            selectedMonth={monthSelected}
                            monthSelectedPosition={monthSelectedPosition}
                            onChange={this.handleMonthSelect}
                            getCalendarService={getCalendarService}
                            monthSelectorVisible={monthSelectorVisible}
                            currentDate={this.currentDate}
                            allowedRange={allowedRange}
                        />
                    </CalendarMonthSelectorWrapperElement>
                </CalendarViewHolderElement>
                {(leftFooter || rightFooter || (submitButtonLabel && submitButtonHandler)) && (
                    <>
                        <Divider />
                        <CalendarFooter
                            spacing="200"
                            distribution="space-between"
                            itemAlignment={!isSingleMonth && rightFooter ? 'center' : undefined}
                            vertical={isSingleMonth}
                        >
                            <SectoredCalendarFooter small={isSingleMonth}>{leftFooter}</SectoredCalendarFooter>
                            {rightFooter ? (
                                <SectoredCalendarFooter small={isSingleMonth}>{rightFooter}</SectoredCalendarFooter>
                            ) : null}
                            {submitButtonLabel && submitButtonHandler && (
                                <Button
                                    onClick={submitButtonHandler}
                                    wide={isSingleMonth}
                                    size={isSingleMonth ? 'large' : 'medium'}
                                    data-test-submit-button
                                    type="submit"
                                >
                                    {submitButtonLabel}
                                </Button>
                            )}
                        </CalendarFooter>
                    </>
                )}
            </div>
        );
    }
}

export default withBreakpointRange(withTheme(Calendar)) as unknown as CalendarType;
